import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService, OAuthEvent } from 'angular-oauth2-oidc';
import { filter } from 'rxjs';
import { HTTP } from './services/http';

/** Vierkant-Betreiber-Rechte: wirken über alle Studios und gelten nie über `*` (backend services/session.py). */
export const OPERATOR_PERMISSIONS: readonly string[] = ['catalog.manage', 'service.user'];

@Injectable({ providedIn: 'root' })
export class AuthService {
  oauth = inject(OAuthService);
  authStateChanged = signal(0);

  private readonly router = inject(Router);

  // Effektive Rechte/Gruppen der Person (vom Backend aufgelöst, via /api/me).
  private readonly perms = signal<Set<string>>(new Set());
  private readonly groups = signal<Set<string>>(new Set());
  private loaded = false;
  /** True only after /me actually answered -- `loaded` is also set when every attempt failed. */
  private confirmed = false;
  /** Whose rights these are (identity `sub`), to notice another user's token from another tab. */
  private rightsOf: string | null = null;
  /** The running /me request -- callers share it instead of starting a second one. */
  private loading: Promise<void> | null = null;
  /** Bumped on logout: a request or retry from before it must not write the old user's rights. */
  private generation = 0;

 constructor() {
    void this.loadMe();
    // Eine geänderte Rolle wirkt ohne Neuladen der Seite, sobald man in den Tab zurückkommt.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.isLoggedIn()) void this.loadMe();
    });
    this.oauth.events
      .pipe(filter((e: OAuthEvent) =>
        e.type === 'token_received' ||
        e.type === 'token_refreshed' ||
        e.type === 'logout'
      ))
      .subscribe((e: OAuthEvent) => {
        if (e.type === 'logout') {
          // Nobody is logged in: no rights -- and "not loaded" again, so the next login fetches them.
          this.resetRights();
        } else if (e.type === 'token_received' && !this.confirmed) {
          // A fresh login: at construction there was no token yet, so the rights were never fetched.
          // Only then -- the library sends `token_received` on every silent refresh too, and a
          // refresh is not a reason to ask /me again.
          void this.loadMe();
        }
        this.authStateChanged.update(v => v + 1);
      });

    // The events above only cover this tab. Since the tokens live in
    // localStorage they are shared, so another tab logging in or out changes
    // our auth state without us noticing - a logged out tab would keep
    // rendering a full UI until its next API call fails.
    window.addEventListener('storage', e => this.syncFromOtherTab(e));
  }

  /** Reacts to a token change made by another tab. Never fires for our own writes. */
  private syncFromOtherTab(event: StorageEvent) {
    if (event.storageArea !== localStorage) return;
    // key === null means the whole storage was cleared
    if (event.key !== null && event.key !== 'access_token') return;

    this.authStateChanged.update(v => v + 1);
    if (!this.isLoggedIn()) {
      this.resetRights();
      this.router.navigate(['']);
    } else if (!this.confirmed || this.currentSub() !== this.rightsOf) {
      // Logged in in the other tab, or as somebody else there. A refresh there (same user) needs no
      // new /me here.
      if (this.confirmed && this.currentSub() !== this.rightsOf) this.resetRights();
      void this.loadMe();
    }
  }

  private resetRights() {
    this.generation++;
    this.loading = null;
    this.perms.set(new Set());
    this.groups.set(new Set());
    this.loaded = false;
    this.confirmed = false;
    this.rightsOf = null;
  }

  private currentSub(): string | null {
    const claims = this.oauth.getIdentityClaims() as { sub?: string } | null;
    return claims?.sub ?? null;
  }

  /** Ob die Rechte vom Backend da sind (oder alle Versuche gescheitert sind). */
  isLoaded(): boolean { return this.loaded; }

  /** Wartet, bis die Rechte da sind -- für Guards: bis dahin gilt sonst "alles erlaubt". */
  whenReady(): Promise<void> {
    if (this.loaded || !this.isLoggedIn()) return Promise.resolve();
    return this.loading ?? this.loadMe();
  }

  /** Rechte neu holen -- nach dem Speichern einer Rolle, beim Zurückkehren in den Tab. */
  public reload(): Promise<void> {
    // A request already on the wire may have left before the change being reloaded for (a role just
    // saved) -- wait for it and ask once more, instead of handing back its old answer.
    if (this.loading) return this.loading.then(() => this.loadMe());
    return this.loadMe();
  }

  /** Effektive Rechte vom Backend holen. Erst nach gültigem Login sinnvoll. Läuft höchstens einmal
   *  gleichzeitig: wer währenddessen fragt, wartet auf denselben Request. */
  private loadMe(): Promise<void> {
    if (this.loading) return this.loading;
    const generation = this.generation;
    const run = this.fetchMe(generation).finally(() => {
      if (this.loading === run) this.loading = null;
    });
    this.loading = run;
    return run;
  }

  private async fetchMe(generation: number) {
    for (let attempt = 0; attempt < 4; attempt++) {
      if (attempt) await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
      if (generation !== this.generation) return;                 // abgemeldet inzwischen
      if (this.oauth.hasValidAccessToken && !this.oauth.hasValidAccessToken()) return;
      try {
        // handled: kein Fehler-Toast je Versuch -- ein Aussetzer ist hier kein Ereignis für den Nutzer
        const me = await HTTP.get<{ permissions?: string[]; groups?: string[] }>('me', undefined, 'json', true);
        if (generation !== this.generation) return;
        this.perms.set(new Set(me?.permissions ?? []));
        this.groups.set(new Set(me?.groups ?? []));
        this.loaded = true;
        this.confirmed = true;
        this.rightsOf = this.currentSub();
        this.authStateChanged.update(v => v + 1);   // Menü/Ansichten neu bewerten
        return;
      } catch {
        // nächster Versuch nach 2, 4, 6 s
      }
    }
    if (generation !== this.generation) return;
    // Alle Versuche gescheitert. Gab es schon gültige Rechte, bleiben sie -- ein Netzaussetzer beim
    // Zurückkehren in den Tab soll nicht das Menü leeren. Beim ersten Laden gilt "keine Rechte",
    // nicht "alles zeigen", sonst sah ein Mitarbeiter nach einem Aussetzer jede Kachel.
    if (!this.loaded) {
      this.perms.set(new Set());
      this.groups.set(new Set());
      this.loaded = true;
      this.authStateChanged.update(v => v + 1);
    }
  }

  isLoggedIn(): boolean {
    return !!this.oauth.hasValidAccessToken();
  }

  logout(): void {
    this.oauth.logOut();
  }

  private has(p: string): boolean {
    const set = this.perms();
    // `*` ist der Studio-Admin -- Betreiber-Rechte (Master-Katalog, Service-User) gelten nur ausdrücklich,
    // wie im Backend (services/auth.has_permission).
    return set.has(p) || (set.has('*') && !OPERATOR_PERMISSIONS.includes(p));
  }

  hasPermission(args: PermissionCombinator | undefined): boolean {
    if (!args) return true;
    const names = typeof args === 'string' ? [args]
      : Array.isArray(args) ? args : [...(args.and ?? []), ...(args.or ?? [])];
    const operator = names.some(p => OPERATOR_PERMISSIONS.includes(p));
    // Solange /api/me noch nicht geladen ist: nichts verstecken (verhindert Flackern beim Start) --
    // außer Betreiber-Bereichen, die sollen nie kurz aufblitzen.
    if (!this.loaded) return !operator;
    if (this.perms().has('*') && !operator) return true;
    if (typeof args === 'string') return this.has(args);
    if (Array.isArray(args)) return args.every(p => this.has(p));        // Array = UND
    if (args.and && args.and.length && !args.and.every(p => this.has(p))) return false;
    if (args.or && args.or.length && !args.or.some(p => this.has(p))) return false;
    return true;
  }

  hasGroup(args: string | string[]): boolean {
    if (!this.loaded) return true;
    if (this.perms().has('*')) return true;   // Admin (Wildcard) sieht alles
    const g = this.groups();
    const arr = Array.isArray(args) ? args : [args];
    return arr.some(x => g.has(x));
  }

  hasFeature(feature: string) {
    const JWT = this.oauth.getIdentityClaims();
    const features: Record<string, boolean> = JWT && JWT.features ? JWT.features : {};
    return !!features[feature];
  }
}

/**
 * Maybe overkill but a type to represent permission combinations by paths
 * The higher the node the more general the permission
 * "ticket" grants access to all ticket related views like "ticket.read", "ticket.write" etc.
 *
 * Allows single checks or combinations like {and: ['ticket.read', 'studios.test']} or {or: ['ticket.read', 'ticket.write']}
 */
export type PermissionCombinator = { and?: string[], or?: string[] } | string[] | string;
